/**
 * What moving the store's dispatch location will do, in the owner's words.
 *
 * Changing the location used to be a plain settings save. It is not: parcels
 * that are ready and trips that have not left follow the store to its new
 * location, and a Rider who has not set off is sent there instead. The owner
 * decides to move; the system does the rest - but the owner should see what
 * "the rest" is before confirming, and learn afterwards what actually happened.
 */

function plural(count, one, many) {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * Sentences describing the operational impact, or an empty list when there is
 * none to warn about.
 *
 * Only a physical move is operational: parcels are collected somewhere else and
 * Riders are sent there. A reworded address at the same pin changes only what
 * Riders read, which is not worth a warning - it is reported after saving.
 */
export function describeOriginChangeImpact(impact) {
  if (!impact?.hasImpact) return [];
  if (impact.changeKind && impact.changeKind !== 'PHYSICAL') return [];
  const lines = [];
  if (impact.readyParcels > 0) {
    lines.push(`${plural(impact.readyParcels, 'ready parcel', 'ready parcels')} will be collected from the new location.`);
  }
  if (impact.plannedRuns > 0) {
    lines.push(`${plural(impact.ridersAffected, 'rider who hasn’t set off', 'riders who haven’t set off')} will be sent to the new location instead.`);
  }
  if (impact.startedRuns > 0) {
    // The one thing the system will not change for them: a Rider already on
    // the road keeps going to the old address, and only the owner can act on
    // that.
    lines.push(`${plural(impact.ridersAlreadyTravelling, 'rider is', 'riders are')} already on the way to your current address and won’t be redirected. Contact them if they should come to the new location.`);
  }
  return lines;
}

/** What happened, after the save. Null when there is nothing worth saying. */
export function describeOriginChangeResult(response) {
  if (response?.unchanged) return 'Nothing changed, so your deliveries were left as they were.';
  const result = response?.reconciliation;
  if (!result) return null;
  if (result.physicalChange === false) {
    // Same place, new wording: nothing is collected anywhere else.
    return result.runsRelabelled > 0
      ? `${plural(result.ridersAffected, 'rider who hasn’t set off', 'riders who haven’t set off')} will see the updated address.`
      : null;
  }
  const parts = [];
  if (result.parcelsRechecked > 0) {
    parts.push(`${plural(result.parcelsRechecked, 'parcel', 'parcels')} now collected from here`);
  }
  if (result.runsReprepared > 0) {
    parts.push(`${plural(result.ridersAffected, 'rider', 'riders')} redirected`);
  }
  if (result.startedRunsUntouched > 0) {
    parts.push(`${plural(result.ridersAlreadyTravelling, 'rider', 'riders')} already on the way to the old address`);
  }
  return parts.length ? `${parts.join(', ')}.`.replace(/^./, (c) => c.toUpperCase()) : null;
}

// The impact lines are built from counts and fixed wording, but they are shown
// as HTML in a dialog, so they are escaped regardless of where they came from.
export function escapeHtml(value) {
  return String(value ?? '')
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}
